import React, { ReactNode, useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import Tippy from '@tippyjs/react';
import { omit } from 'lodash';
import dayjs from 'dayjs';
import { FormattedMessage } from 'react-intl';
import envConfig from '@nile/home/src/Config/env';
import { Button } from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';

import {
  DATE_TIME_FORMAT,
  DateTimeFormatter,
  Loader,
  NoDataCard,
  convertToMSPTimeZone,
  useIntersectionObserver,
  withErrorBoundary
} from '@nile/components';
import { Audit, Listinfo } from '@nile/types';
import { GET_AUDIT_LOG_LIST } from '@nile/graphql';
import AuditLog from '../../Components/Audit/AuditLog';

function AuditLogs(props): ReactNode {
  const [auditList, setAuditList] = useState<Audit[]>([]);
  const [auditListLoading, setAuditListLoading] = useState(false);
  const [listinfo, setListinfo] = useState<Listinfo | null>(null);
  const [auditDateMap, setAuditDateMap] = useState({});

  const [
    getAuditsList,
    { data: auditListData, loading, error: auditListError }
  ] = useLazyQuery(GET_AUDIT_LOG_LIST, {
    fetchPolicy: 'cache-and-network',
    onCompleted: (res) => {
      if (res?.getTicketAuditLogList) {
        setAuditList([...auditList, ...res?.getTicketAuditLogList?.audits]);
        setListinfo(res?.getTicketAuditLogList?.listInfo);
        setAuditListLoading(false);
      }
    }
  });

  const ref = useIntersectionObserver(() => {
    setAuditListLoading(true);
    getAuditsList({
      variables: {
        input: {
          entityId: props?.ticket?.ticketId,
          listInfo: {
            ...omit(listinfo, ['hasMore']),
            page: listinfo?.page + 1,
            pageSize: 50,
            cursor: {
              attribute: 'time',
              type: 'BEFORE',
              value: auditList?.length
                ? auditList[auditList?.length - 1]?.time
                : dayjs(convertToMSPTimeZone(new Date())).format(
                    DATE_TIME_FORMAT
                  )
            }
          }
        }
      }
    });
  }, [auditListLoading]);

  function fetchInitialAuditList(): void {
    setAuditListLoading(true);
    getAuditsList({
      variables: {
        input: {
          entityId: props?.ticket?.ticketId,
          listInfo: {
            page: 1,
            pageSize: 50,
            sort: {
              attribute: 'time',
              order: 'DESC'
            }
          }
        }
      }
    });
  }

  useEffect(() => {
    fetchInitialAuditList();

    // EventEmitter.subscribe('refetchAudit', () => {
    //   setAuditList([]);
    //   setListinfo(null);
    //   fetchInitialAuditList();
    // });

    // return () => {
    //   EventEmitter.unsubscribe('refetchAudit');
    // };
  }, []);

  if (auditListLoading && !auditList?.length) {
    return (
      <div className="fhp w100 ghost-white" id="audit-loader">
        <Loader />
      </div>
    );
  }

  const handleDateToggle = (formattedDate): void => {
    setAuditDateMap({
      ...auditDateMap,
      [formattedDate]:
        formattedDate in auditDateMap ? !auditDateMap[formattedDate] : false
    });
  };

  if (!auditListLoading && !auditList?.length) {
    return (
      <div className="flex full-height center jcenter mt25 noDataCard">
        <NoDataCard
          imageURL={`url(${envConfig.imageRoot}svg/not-found.svg)`}
          description={<FormattedMessage id="audit.log.empty.description" />}
          // description={emptyState?.description}
        />
      </div>
    );
  }

  return (
    <div className="fx col p10 g10 f-12 ghost-white">
      {auditList.map(
        (audit: Audit, i: number): ReactNode => {
          const currentDate = audit?.time;
          const previousDate = auditList[i - 1]?.time;
          const isSameDate = dayjs(currentDate).isSame(previousDate, 'day');
          const formattedDate = dayjs(audit?.time).format('DDMMYY');

          return (
            <>
              {(i === 0 || !isSameDate) && (
                <div className="fx actr">
                  <Button onClick={() => handleDateToggle(formattedDate)}>
                    <Tippy
                      content={
                        <div className="p0510">
                          <DateTimeFormatter
                            type="date"
                            date={convertToMSPTimeZone(audit?.time)}
                          />
                        </div>
                      }
                      duration={250}
                      delay={500}
                      interactive
                      placement="bottom-start"
                      arrow={false}
                      allowHTML
                      theme="dark"
                      appendTo={document.body}
                    >
                      <span className="fx actr f-12">
                        <DateTimeFormatter
                          type="onlyDate"
                          date={audit?.time}
                          relative
                        />
                        <SVGIcon
                          name={
                            (formattedDate in auditDateMap &&
                              auditDateMap[formattedDate]) ||
                            !(formattedDate in auditDateMap)
                              ? 'ArrowDown'
                              : 'ArrowRight'
                          }
                          size="sm"
                        />
                      </span>
                    </Tippy>
                  </Button>
                </div>
              )}
              {(formattedDate in auditDateMap && auditDateMap[formattedDate]) ||
              !(formattedDate in auditDateMap) ? (
                <AuditLog audit={audit} />
              ) : null}
            </>
          );
        }
      )}
      {listinfo?.hasMore && (
        <>
          <div style={{ height: '150px' }}>
            <Loader />
          </div>
          {!auditListLoading && (
            <div className="op-0" style={{ height: '10px' }} ref={ref} />
          )}
        </>
      )}
    </div>
  );
}

export default withErrorBoundary(AuditLogs);
